export function dateInZone(zone, now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const value = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

export function isFutureDate(value, zone, now = new Date()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T12:00:00Z`);
  if (!Number.isFinite(parsed.valueOf()) || parsed.toISOString().slice(0, 10) !== value) return false;
  try { return value > dateInZone(zone, now); } catch { return false; }
}

export function validZone(value) {
  if (!value.trim()) return false;
  try { dateInZone(value.trim()); return true; } catch { return false; }
}

const BaseElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;
class EchoesGiftFields extends BaseElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.mode = this.querySelector('[data-echoes-mode]');
    this.modeValue = this.querySelector('[data-echoes-mode-value]');
    this.details = this.querySelector('[data-echoes-details]');
    this.timing = this.querySelector('[data-echoes-timing]');
    this.future = this.querySelector('[data-echoes-future]');
    this.date = this.querySelector('[data-echoes-date]');
    this.zone = this.querySelector('[data-echoes-zone]');
    this.form = this.closest('form');
    if (!this.mode || !this.modeValue || !this.details || !this.timing || !this.future || !this.date || !this.zone || !this.form) return;
    this.mode.disabled = false;
    const zones = this.querySelector('[data-echoes-zones]');
    if (zones && !zones.childElementCount) {
      let names = ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu'];
      if (typeof Intl.supportedValuesOf === 'function') names = [...new Set(['UTC', ...Intl.supportedValuesOf('timeZone')])];
      for (const name of names) {
        const option = document.createElement('option');
        option.value = name;
        zones.append(option);
      }
    }
    this.addEventListener('change', () => this.sync(), { signal });
    this.addEventListener('input', () => this.sync(), { signal });
    this.form.addEventListener('submit', event => {
      this.zone.value = this.zone.value.trim();
      const recipientName = this.querySelector('input[name="properties[Recipient first name]"]');
      if (recipientName) recipientName.value = recipientName.value.trim();
      this.sync();
      if (!this.form.checkValidity()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.form.reportValidity();
      }
    }, { capture: true, signal });
    this.sync();
  }

  disconnectedCallback() { this.abort?.abort(); }

  sync() {
    const gift = this.mode.value === 'Yes';
    const scheduled = gift && this.timing.value === 'Future date';
    this.modeValue.value = gift ? 'Yes' : 'No';
    this.details.hidden = !gift;
    this.details.disabled = !gift;
    this.future.hidden = !scheduled;
    this.future.disabled = !scheduled;
    this.zone.setCustomValidity('');
    this.date.setCustomValidity('');
    this.date.removeAttribute('min');
    if (scheduled && this.zone.value.trim()) {
      if (!validZone(this.zone.value)) this.zone.setCustomValidity(this.dataset.zoneError);
      else if (this.date.value && !isFutureDate(this.date.value, this.zone.value.trim())) this.date.setCustomValidity(this.dataset.dateError);
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('echoes-gift-fields')) customElements.define('echoes-gift-fields', EchoesGiftFields);
